/**
 * CourseUnitsTab — sortable unit list with nested sortable lesson rows.
 * Expanding a unit shows its lessons; expanding a lesson shows materials + assignments.
 */
import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
  Loader2,
  BookOpen,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { UnitForm, type UnitFormValues } from '@client/components/forms/unit-form';
import { CourseLessonForm, type CourseLessonFormValues } from '@client/components/forms/course-lesson-form';
import { CourseAssignmentForm, type CourseAssignmentFormValues } from '@client/components/forms/course-assignment-form';
import { CourseMaterialList } from './course-material-list';
import { DragHandle, SortableList } from './sortable-list';
import {
  useCourseUnits,
  useCreateCourseUnit,
  useUpdateCourseUnit,
  useDeleteCourseUnit,
  useReorderCourseUnits,
  useCourseLessons,
  useCreateCourseLesson,
  useUpdateCourseLesson,
  useDeleteCourseLesson,
  useReorderCourseLessons,
  useCourseAssignments,
  useCreateCourseAssignment,
  useUpdateCourseAssignment,
  useDeleteCourseAssignment,
  type CourseUnit,
  type CourseLesson,
  type CourseAssignment,
} from '@client/lib/queries/use-curriculum';

// ---------------------------------------------------------------------------
// Assignment row + section (per lesson)
// ---------------------------------------------------------------------------
const ASSIGNMENT_TYPE_LABELS: Record<string, string> = {
  homework: 'Bài tập',
  quiz: 'Kiểm tra',
  project: 'Dự án',
};

interface AssignmentSectionProps {
  unitId: string;
  courseLessonId?: string;
}

function AssignmentSection({ unitId, courseLessonId }: AssignmentSectionProps) {
  const { data: assignments, isLoading } = useCourseAssignments({ unitId });
  const filtered = (assignments ?? []).filter((a) =>
    courseLessonId ? a.courseLessonId === courseLessonId : true,
  );

  const createAssignment = useCreateCourseAssignment();
  const deleteAssignment = useDeleteCourseAssignment();

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CourseAssignment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CourseAssignment | null>(null);

  const updateAssignment = useUpdateCourseAssignment(editTarget?.id ?? '');

  async function handleCreate(values: CourseAssignmentFormValues) {
    try {
      await createAssignment.mutateAsync({
        unitId,
        courseLessonId: courseLessonId ?? undefined,
        type: values.type,
        title: values.title,
        description: values.description || undefined,
        maxScore: values.maxScore !== '' && values.maxScore !== undefined ? Number(values.maxScore) : undefined,
        dueDaysAfterLesson: values.dueDaysAfterLesson !== '' && values.dueDaysAfterLesson !== undefined
          ? Number(values.dueDaysAfterLesson) : undefined,
      });
      toast.success('Đã thêm bài tập.');
      setAddOpen(false);
    } catch {
      toast.error('Không thêm được bài tập.');
    }
  }

  async function handleUpdate(values: CourseAssignmentFormValues) {
    if (!editTarget) return;
    try {
      await updateAssignment.mutateAsync({
        type: values.type,
        title: values.title,
        description: values.description || undefined,
        maxScore: values.maxScore !== '' && values.maxScore !== undefined ? Number(values.maxScore) : undefined,
        dueDaysAfterLesson: values.dueDaysAfterLesson !== '' && values.dueDaysAfterLesson !== undefined
          ? Number(values.dueDaysAfterLesson) : undefined,
      });
      toast.success('Đã cập nhật bài tập.');
      setEditTarget(null);
    } catch {
      toast.error('Không cập nhật được bài tập.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteAssignment.mutateAsync(deleteTarget.id);
      toast.success('Đã xóa bài tập.');
    } catch {
      toast.error('Không xóa được bài tập.');
    } finally {
      setDeleteTarget(null);
    }
  }

  if (isLoading) return <Skeleton className="h-8 w-full" />;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Bài tập ({filtered.length})
        </span>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setAddOpen(true)}>
          <Plus className="h-3 w-3" /> Thêm
        </Button>
      </div>

      {filtered.map((a) => (
        <div
          key={a.id}
          className="flex items-center gap-2 px-3 py-1.5 rounded border border-border bg-card text-sm group"
        >
          <Badge variant="secondary" className="text-xs px-1.5 py-0 shrink-0">
            {ASSIGNMENT_TYPE_LABELS[a.type] ?? a.type}
          </Badge>
          <span className="flex-1 min-w-0 truncate">{a.title}</span>
          {a.maxScore != null && (
            <span className="text-xs text-muted-foreground shrink-0">{a.maxScore}đ</span>
          )}
          {a.dueDaysAfterLesson != null && (
            <span className="text-xs text-muted-foreground shrink-0">{a.dueDaysAfterLesson}d</span>
          )}
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditTarget(a)}>
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:text-destructive"
              onClick={() => setDeleteTarget(a)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <p className="text-xs text-muted-foreground py-1">Chưa có bài tập nào.</p>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Thêm bài tập</DialogTitle></DialogHeader>
          <CourseAssignmentForm
            isSubmitting={createAssignment.isPending}
            onSubmit={handleCreate}
            onCancel={() => setAddOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sửa bài tập</DialogTitle></DialogHeader>
          {editTarget && (
            <CourseAssignmentForm
              defaultValues={{
                type: editTarget.type,
                title: editTarget.title,
                description: editTarget.description ?? '',
                maxScore: editTarget.maxScore ?? '',
                dueDaysAfterLesson: editTarget.dueDaysAfterLesson ?? '',
              }}
              isSubmitting={updateAssignment.isPending}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
          <DialogHeader><DialogTitle>Xóa bài tập?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Xóa <strong>{deleteTarget?.title}</strong>? Không thể hoàn tác.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Hủy</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteAssignment.isPending}>
              {deleteAssignment.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Xóa'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lesson row (collapsible, shows materials + assignments when expanded)
// ---------------------------------------------------------------------------
interface LessonRowProps {
  lesson: CourseLesson & { _dragHandleProps?: React.HTMLAttributes<HTMLElement> };
  unitId: string;
  onEdit: (l: CourseLesson) => void;
  onDelete: (l: CourseLesson) => void;
}

function LessonRow({ lesson, unitId, onEdit, onDelete }: LessonRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded border border-border bg-background mb-2">
      {/* Lesson header */}
      <div className="flex items-center gap-2 px-3 py-2 group">
        <DragHandle {...(lesson._dragHandleProps ?? {})} />
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Thu gọn' : 'Mở rộng'}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        <span className="text-xs text-muted-foreground w-5 shrink-0 text-right font-mono">
          {lesson.orderNo}
        </span>

        <button
          type="button"
          className="flex-1 min-w-0 text-left text-sm font-medium hover:text-primary truncate"
          onClick={() => setExpanded((v) => !v)}
        >
          {lesson.title}
        </button>

        {lesson.suggestedDurationMin && (
          <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <Clock className="h-3 w-3" />
            {lesson.suggestedDurationMin}p
          </span>
        )}

        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(lesson)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:text-destructive"
            onClick={() => onDelete(lesson)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-border px-4 py-3 flex flex-col gap-4 bg-muted/20">
          {/* Pedagogy preview */}
          {(lesson.learningObjectives || lesson.teacherNotes || lesson.homeworkSummary) && (
            <div className="flex flex-col gap-2">
              {lesson.learningObjectives && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                    Mục tiêu
                  </p>
                  <p className="text-xs text-foreground line-clamp-3">{lesson.learningObjectives}</p>
                </div>
              )}
              {lesson.teacherNotes && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                    Ghi chú GV
                  </p>
                  <p className="text-xs text-foreground line-clamp-2">{lesson.teacherNotes}</p>
                </div>
              )}
            </div>
          )}

          {/* Materials */}
          <CourseMaterialList courseLessonId={lesson.id} />

          {/* Assignments */}
          <AssignmentSection unitId={unitId} courseLessonId={lesson.id} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lesson list (sortable) for one unit
// ---------------------------------------------------------------------------
interface LessonListProps {
  unit: CourseUnit;
}

function LessonList({ unit }: LessonListProps) {
  const { data: lessons, isLoading, isError } = useCourseLessons(unit.id);
  const createLesson = useCreateCourseLesson();
  const reorder = useReorderCourseLessons(unit.id);
  const deleteLesson = useDeleteCourseLesson();

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CourseLesson | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CourseLesson | null>(null);
  const updateLesson = useUpdateCourseLesson(editTarget?.id ?? '');

  async function handleCreate(values: CourseLessonFormValues) {
    const nextOrder = (lessons?.length ?? 0) + 1;
    try {
      await createLesson.mutateAsync({
        unitId: unit.id,
        orderNo: nextOrder,
        title: values.title,
        description: values.description || undefined,
        content: values.content || undefined,
        learningObjectives: values.learningObjectives || undefined,
        teacherNotes: values.teacherNotes || undefined,
        homeworkSummary: values.homeworkSummary || undefined,
        suggestedDurationMin:
          values.suggestedDurationMin !== '' && values.suggestedDurationMin !== undefined
            ? Number(values.suggestedDurationMin)
            : undefined,
      });
      toast.success('Đã thêm bài học.');
      setAddOpen(false);
    } catch {
      toast.error('Không thêm được bài học.');
    }
  }

  async function handleUpdate(values: CourseLessonFormValues) {
    if (!editTarget) return;
    try {
      await updateLesson.mutateAsync({
        title: values.title,
        description: values.description || undefined,
        content: values.content || undefined,
        learningObjectives: values.learningObjectives || undefined,
        teacherNotes: values.teacherNotes || undefined,
        homeworkSummary: values.homeworkSummary || undefined,
        suggestedDurationMin:
          values.suggestedDurationMin !== '' && values.suggestedDurationMin !== undefined
            ? Number(values.suggestedDurationMin)
            : undefined,
      });
      toast.success('Đã cập nhật bài học.');
      setEditTarget(null);
    } catch {
      toast.error('Không cập nhật được bài học.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteLesson.mutateAsync(deleteTarget.id);
      toast.success('Đã xóa bài học.');
    } catch {
      toast.error('Không xóa được bài học.');
    } finally {
      setDeleteTarget(null);
    }
  }

  function handleReorder(newLessons: CourseLesson[]) {
    reorder.mutate(newLessons.map((l, idx) => ({ id: l.id, orderNo: idx + 1 })));
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Không tải được bài học.</AlertDescription>
      </Alert>
    );
  }

  const sorted = [...(lessons ?? [])].sort((a, b) => a.orderNo - b.orderNo);

  return (
    <div className="flex flex-col gap-1">
      {sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Chưa có bài học. Thêm bài học đầu tiên.</p>
      ) : (
        <SortableList
          items={sorted}
          onReorder={handleReorder}
          renderItem={(lesson) => (
            <LessonRow
              lesson={lesson as CourseLesson & { _dragHandleProps?: React.HTMLAttributes<HTMLElement> }}
              unitId={unit.id}
              onEdit={(l) => setEditTarget(l)}
              onDelete={(l) => setDeleteTarget(l)}
            />
          )}
        />
      )}

      <Button
        variant="ghost"
        size="sm"
        className="self-start mt-1 h-8 text-xs gap-1"
        onClick={() => setAddOpen(true)}
      >
        <Plus className="h-3 w-3" /> Thêm bài học
      </Button>

      {/* Add lesson dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Thêm bài học</DialogTitle></DialogHeader>
          <CourseLessonForm
            isSubmitting={createLesson.isPending}
            onSubmit={handleCreate}
            onCancel={() => setAddOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit lesson dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sửa bài học</DialogTitle></DialogHeader>
          {editTarget && (
            <CourseLessonForm
              defaultValues={{
                title: editTarget.title,
                description: editTarget.description ?? '',
                content: editTarget.content ?? '',
                learningObjectives: editTarget.learningObjectives ?? '',
                teacherNotes: editTarget.teacherNotes ?? '',
                homeworkSummary: editTarget.homeworkSummary ?? '',
                suggestedDurationMin: editTarget.suggestedDurationMin ?? '',
              }}
              isSubmitting={updateLesson.isPending}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
          <DialogHeader><DialogTitle>Xóa bài học?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Xóa <strong>{deleteTarget?.title}</strong>? Học liệu đính kèm cũng sẽ bị xóa.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Hủy</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteLesson.isPending}>
              {deleteLesson.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Xóa'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unit card (collapsible)
// ---------------------------------------------------------------------------
interface UnitCardProps {
  unit: CourseUnit & { _dragHandleProps?: React.HTMLAttributes<HTMLElement> };
  onEdit: (u: CourseUnit) => void;
  onDelete: (u: CourseUnit) => void;
}

function UnitCard({ unit, onEdit, onDelete }: UnitCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="mb-3 shadow-none">
      <CardHeader className="p-0">
        <div className="flex items-center gap-2 px-4 py-3 group">
          <DragHandle {...(unit._dragHandleProps ?? {})} />
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Thu gọn' : 'Mở rộng'}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />

          <button
            type="button"
            className="flex-1 min-w-0 text-left"
            onClick={() => setExpanded((v) => !v)}
          >
            <span className="font-semibold text-sm">{unit.name}</span>
          </button>

          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(unit)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:text-destructive"
              onClick={() => onDelete(unit)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 px-4 pb-4 border-t border-border">
          {unit.description && (
            <p className="text-sm text-muted-foreground mt-3 mb-3">{unit.description}</p>
          )}
          <LessonList unit={unit} />
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main export — CourseUnitsTab
// ---------------------------------------------------------------------------
interface CourseUnitsTabProps {
  courseId: string;
}

export function CourseUnitsTab({ courseId }: CourseUnitsTabProps) {
  const { data: units, isLoading, isError } = useCourseUnits(courseId);
  const createUnit = useCreateCourseUnit();
  const reorder = useReorderCourseUnits(courseId);
  const deleteUnit = useDeleteCourseUnit();

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CourseUnit | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CourseUnit | null>(null);
  const updateUnit = useUpdateCourseUnit(editTarget?.id ?? '');

  async function handleCreate(values: UnitFormValues) {
    const nextOrder = (units?.length ?? 0) + 1;
    try {
      await createUnit.mutateAsync({
        courseId,
        orderNo: nextOrder,
        name: values.name,
        description: values.description || undefined,
        objectives: values.objectives || undefined,
      });
      toast.success('Đã thêm chương.');
      setAddOpen(false);
    } catch {
      toast.error('Không thêm được chương.');
    }
  }

  async function handleUpdate(values: UnitFormValues) {
    if (!editTarget) return;
    try {
      await updateUnit.mutateAsync({
        name: values.name,
        description: values.description || undefined,
        objectives: values.objectives || undefined,
      });
      toast.success('Đã cập nhật chương.');
      setEditTarget(null);
    } catch {
      toast.error('Không cập nhật được chương.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteUnit.mutateAsync(deleteTarget.id);
      toast.success('Đã xóa chương.');
    } catch {
      toast.error('Không xóa được chương.');
    } finally {
      setDeleteTarget(null);
    }
  }

  function handleReorder(newUnits: CourseUnit[]) {
    reorder.mutate(newUnits.map((u, idx) => ({ id: u.id, orderNo: idx + 1 })));
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Không tải được danh sách chương.</AlertDescription>
      </Alert>
    );
  }

  const sorted = [...(units ?? [])].sort((a, b) => a.orderNo - b.orderNo);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold">Chương &amp; Bài học ({sorted.length})</h3>
        <Button size="sm" className="gap-1" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" /> Thêm chương
        </Button>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Chưa có chương nào. Bắt đầu bằng cách thêm chương đầu tiên.</p>
        </div>
      ) : (
        <SortableList
          items={sorted}
          onReorder={handleReorder}
          renderItem={(unit) => (
            <UnitCard
              unit={unit as CourseUnit & { _dragHandleProps?: React.HTMLAttributes<HTMLElement> }}
              onEdit={(u) => setEditTarget(u)}
              onDelete={(u) => setDeleteTarget(u)}
            />
          )}
        />
      )}

      {/* Add unit dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Thêm chương</DialogTitle></DialogHeader>
          <UnitForm
            isSubmitting={createUnit.isPending}
            onSubmit={handleCreate}
            onCancel={() => setAddOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit unit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sửa chương</DialogTitle></DialogHeader>
          {editTarget && (
            <UnitForm
              defaultValues={{
                name: editTarget.name,
                description: editTarget.description ?? '',
                objectives: editTarget.objectives ?? '',
              }}
              isSubmitting={updateUnit.isPending}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
          <DialogHeader><DialogTitle>Xóa chương?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Xóa <strong>{deleteTarget?.name}</strong>? Tất cả bài học và học liệu bên trong cũng sẽ bị xóa.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Hủy</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteUnit.isPending}>
              {deleteUnit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Xóa'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
